import { Stack, Typography } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { Loader } from '@/components/layout/loading';
import { OptionMenu } from '@/components/option-menu/option-menu';
import type { OptionMenuItemType } from '@/components/option-menu/option-menu';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';

const CONTROLLED_BY_EON = 'Controlled by Eon (recommended)';
const LIST_OBJECTS = 'List objects';
const S3_INVENTORY = 'S3 inventory';

interface ConfigField {
  displayText: string;
  menuOptions: OptionMenuItemType[];
}

export const S3BucketScanTypeField = ({
  item,
}: {
  item: InventoryResource;
}) => {
  const dal = useDAL();
  const { snackbar } = useWorkspace();
  const {
    data: itemConfigBody,
    mutate,
    error,
  } = dal.inventory.configuration.useConfig(item.id);

  const { isAuthorized } = useRoles();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async ({
    enabled,
    systemControlled,
  }: {
    enabled: boolean | null;
    systemControlled: boolean;
  }) => {
    if (!itemConfigBody) return;

    setIsUpdating(true);
    try {
      await dal.inventory.configuration.update(item.id, {
        inventoryBackup: {
          ...(enabled !== null && { enabled }),
          systemControlled,
        },
      });
      await mutate();
    } catch (error) {
      const errorMessage = await extractErrorMessage(error);
      snackbar.showErrorMessage(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const getScanTypeField = (): ConfigField => {
    if (error) {
      return {
        displayText: 'N/A',
        menuOptions: [],
      };
    }

    if (!itemConfigBody) {
      return {
        displayText: 'Loading...',
        menuOptions: [],
      };
    }

    const { inventoryBackup } = itemConfigBody;
    if (
      !inventoryBackup ||
      typeof inventoryBackup.systemControlled !== 'boolean'
    ) {
      return {
        displayText: 'N/A',
        menuOptions: [],
      };
    }
    const isUserControlled = !inventoryBackup.systemControlled;

    const getDisplayText = () => {
      const scanType = inventoryBackup.enabled
        ? 'S3 Inventory'
        : 'List objects';

      if (!isUserControlled) {
        return `${scanType} (Controlled by Eon)`;
      }
      return scanType;
    };

    const isInventoryEnabled = inventoryBackup.enabled;
    const userControlledOptions: OptionMenuItemType[] = [
      {
        id: 'regular',
        text: LIST_OBJECTS,
        menuItemProps: {
          selected: isUserControlled && !isInventoryEnabled,
          disabled:
            !isAuthorized('update:inventory') ||
            (isUserControlled && !isInventoryEnabled),
          onClick: () =>
            void handleUpdate({
              enabled: false,
              systemControlled: false,
            }),
        },
      },
      {
        id: 's3-inventory',
        text: S3_INVENTORY,
        menuItemProps: {
          selected: isUserControlled && isInventoryEnabled,
          disabled:
            !isAuthorized('update:inventory') ||
            (isUserControlled && isInventoryEnabled),
          onClick: () =>
            void handleUpdate({
              enabled: true,
              systemControlled: false,
            }),
        },
      },
    ];

    const menuOptions: OptionMenuItemType[] = [
      {
        id: 'system-controlled',
        text: CONTROLLED_BY_EON,
        menuItemProps: {
          selected: !isUserControlled,
          disabled: !isAuthorized('update:inventory') || !isUserControlled,
          onClick: () =>
            void handleUpdate({
              enabled: null,
              systemControlled: true,
            }),
        },
      },
      ...userControlledOptions,
    ];

    return {
      displayText: getDisplayText(),
      menuOptions,
    };
  };

  const scanTypeField = getScanTypeField();

  return (
    <Stack gap='8px' direction='row' alignItems='center'>
      <Typography>{scanTypeField.displayText}</Typography>
      {isUpdating && <Loader variant='inline' />}
      {itemConfigBody &&
        !isUpdating &&
        scanTypeField.menuOptions.length > 0 && (
          <OptionMenu options={scanTypeField.menuOptions} />
        )}
    </Stack>
  );
};
